/**
 * 👤 Profile Page
 * User account and settings with Quick Reorder!
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Receipt, CreditCard, Ticket, Users, Bell, HelpCircle, LogOut, Zap, Check, Gift, Trophy, Clock, X
} from 'lucide-react';
import { useUserStore, useOrderStore, useCartStore, useContestStore } from '../store';
import styles from './Profile.module.css';

// Map item IDs to their images
const itemImages: Record<string, string> = {
  'FM001': '/softserve-tran.png',
  'FM002': '/softserve-swirl.png',
  'FM003': '/softserve-transchocdip.png',
  'FM004': '/softserve-tranvanillalicious.png',
};

const getItemImage = (menuItemId: string) => {
  return itemImages[menuItemId] || '/softserve-tran.png';
};

const menuItems = [
  { id: 'orders', icon: Receipt, title: 'Order History', subtitle: 'View past orders' },
  { id: 'payment', icon: CreditCard, title: 'Payment Methods', subtitle: 'Manage cards' },
  { id: 'seat', icon: Ticket, title: 'My Seat', subtitle: 'Not set' },
  { id: 'family', icon: Users, title: 'Family Group', subtitle: 'Create or join a group' },
  { id: 'notifications', icon: Bell, title: 'Notifications', subtitle: 'On' },
  { id: 'help', icon: HelpCircle, title: 'Help & Support', subtitle: '' },
];

export function ProfilePage() {
  const navigate = useNavigate();
  const userId = useUserStore((state) => state.userId);
  const seatInfo = useUserStore((state) => state.seatInfo);
  const logout = useUserStore((state) => state.logout);
  const orderHistory = useOrderStore((state) => state.orderHistory);
  const addItem = useCartStore((state) => state.addItem);
  const contests = useContestStore((state) => state.contests);
  const getActiveContest = useContestStore((state) => state.getActiveContest);

  const [reorderSuccess, setReorderSuccess] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState('');
  const [showWinnerModal, setShowWinnerModal] = useState(false);
  const [wonContest, setWonContest] = useState<any>(null);

  // Get the most recent completed order for quick reorder
  const lastOrder = orderHistory.length > 0 ? orderHistory[0] : null;
  const activeContest = getActiveContest();

  // Check if user won any contest
  useEffect(() => {
    const myOrderIds = orderHistory.map((o) => o.id);
    const wonContests = contests.filter(
      (c) => c.winnerId && myOrderIds.includes(c.winnerId) && !c.isActive
    );
    if (wonContests.length > 0) {
      const latestWin = wonContests[0];
      // Only show if not dismissed (use sessionStorage)
      const dismissed = sessionStorage.getItem(`dismissed-${latestWin.id}`);
      if (!dismissed) {
        setWonContest(latestWin);
        setShowWinnerModal(true);
      }
    }
  }, [contests, orderHistory]);

  // Update countdown timer
  useEffect(() => {
    if (!activeContest) return;

    const updateTimer = () => {
      const diff = new Date(activeContest.endTime).getTime() - Date.now();
      if (diff <= 0) {
        setTimeRemaining('Drawing winner...');
        return;
      }
      const mins = Math.floor(diff / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      setTimeRemaining(mins > 0 ? `${mins}m ${secs}s` : `${secs}s`);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [activeContest]);

  const dismissWinnerModal = () => {
    if (wonContest) {
      sessionStorage.setItem(`dismissed-${wonContest.id}`, 'true');
    }
    setShowWinnerModal(false);
  };

  const handleQuickReorder = () => {
    if (!lastOrder) return;

    // Add all items from last order to cart
    lastOrder.items.forEach((item) => {
      addItem({
        menuItemId: item.menuItemId,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        size: item.size,
        container: item.container,
        toppings: item.toppings,
        imageUrl: item.imageUrl,
      });
    });

    // Show success feedback
    setReorderSuccess(true);
    setTimeout(() => {
      setReorderSuccess(false);
      navigate('/cart');
    }, 800);
  };

  const handleLogout = () => {
    if (confirm('Are you sure you want to sign out?')) {
      logout();
      navigate('/');
    }
  };

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.title}>Profile</h1>
      </header>

      {/* Winner Modal */}
      {showWinnerModal && wonContest && (
        <div className={styles.winnerOverlay} onClick={dismissWinnerModal}>
          <div className={styles.winnerModal} onClick={(e) => e.stopPropagation()}>
            <button className={styles.winnerClose} onClick={dismissWinnerModal}>
              <X size={24} />
            </button>
            <div className={styles.winnerConfetti}>🎉</div>
            <div className={styles.winnerTrophy}>
              <Trophy size={48} />
            </div>
            <h2 className={styles.winnerTitle}>YOU WON!</h2>
            <div className={styles.winnerPrize}>
              <span className={styles.winnerEmoji}>{wonContest.prizeEmoji}</span>
              <span className={styles.winnerPrizeText}>{wonContest.prize}</span>
            </div>
            <p className={styles.winnerInstructions}>
              Show this screen to staff at the pickup window to claim your prize!
            </p>
            <button className={styles.winnerClaimBtn} onClick={dismissWinnerModal}>
              Awesome!
            </button>
          </div>
        </div>
      )}

      {/* Active Contest Banner */}
      {activeContest && (
        <div className={styles.contestBanner}>
          <div className={styles.contestBannerIcon}>
            <Gift size={24} />
          </div>
          <div className={styles.contestBannerInfo}>
            <span className={styles.contestBannerTitle}>
              {activeContest.prizeEmoji} Win {activeContest.prize}!
            </span>
            <span className={styles.contestBannerSub}>
              Order now to enter! Drawing in {timeRemaining}
            </span>
          </div>
          <div className={styles.contestBannerTimer}>
            <Clock size={16} />
            <span>{timeRemaining}</span>
          </div>
        </div>
      )}

      {/* Quick Reorder Section */}
      {lastOrder && (
        <div className={styles.quickReorderSection}>
          <div className={styles.quickReorderHeader}>
            <Zap size={18} className={styles.quickReorderIcon} />
            <span className={styles.quickReorderTitle}>Quick Reorder</span>
          </div>

          <button
            className={`${styles.quickReorderCard} ${reorderSuccess ? styles.quickReorderSuccess : ''}`}
            onClick={handleQuickReorder}
            disabled={reorderSuccess}
          >
            {/* Item thumbnails */}
            <div className={styles.quickReorderThumbs}>
              {lastOrder.items.slice(0, 3).map((item, index) => (
                <div
                  key={item.id || index}
                  className={styles.quickReorderThumb}
                  style={{ zIndex: 3 - index }}
                >
                  <img
                    src={getItemImage(item.menuItemId)}
                    alt={item.name}
                    className={styles.quickReorderImg}
                  />
                </div>
              ))}
            </div>

            {/* Order info */}
            <div className={styles.quickReorderInfo}>
              <span className={styles.quickReorderItems}>
                {lastOrder.items.map((item, i) => (
                  <span key={i}>
                    {item.quantity}x {item.name}
                    {i < lastOrder.items.length - 1 && ', '}
                  </span>
                ))}
              </span>
              <span className={styles.quickReorderPrice}>${lastOrder.total.toFixed(2)}</span>
            </div>

            {/* Reorder button */}
            <div className={styles.quickReorderBtn}>
              {reorderSuccess ? (
                <Check size={20} />
              ) : (
                <>
                  <Zap size={16} />
                  <span>Reorder</span>
                </>
              )}
            </div>
          </button>
        </div>
      )}

      {/* Profile Card */}
      <div className={styles.profileCard}>
        <div className={styles.avatar}>
          <span>👤</span>
        </div>
        <div className={styles.profileInfo}>
          <h2 className={styles.profileName}>
            {userId ? `Fan #${userId.slice(-6)}` : 'Guest'}
          </h2>
          <p className={styles.profileSubtitle}>Queensland Stadium</p>
        </div>
      </div>

      {/* Menu Items */}
      <div className={styles.menuSection}>
        {menuItems.map((item) => {
          const Icon = item.icon;
          let subtitle = item.subtitle;

          if (item.id === 'seat' && seatInfo) {
            subtitle = `${seatInfo.section}, Row ${seatInfo.row}, Seat ${seatInfo.number}`;
          }

          const handleClick = () => {
            if (item.id === 'orders') {
              navigate('/orders');
            }
          };

          return (
            <button key={item.id} className={styles.menuItem} onClick={handleClick}>
              <div className={styles.menuIcon}>
                <Icon size={20} />
              </div>
              <div className={styles.menuContent}>
                <span className={styles.menuTitle}>{item.title}</span>
                {subtitle && <span className={styles.menuSubtitle}>{subtitle}</span>}
              </div>
              <span className={styles.chevron}>›</span>
            </button>
          );
        })}
      </div>

      {/* App Info */}
      <div className={styles.appInfo}>
        <p className={styles.appName}>🍦 Frosty Flow</p>
        <p className={styles.appVersion}>Version 1.0.0</p>
        <p className={styles.appTagline}>Queensland Stadium App</p>
      </div>

      {/* Sign Out */}
      <button className={styles.signOutButton} onClick={handleLogout}>
        <LogOut size={18} />
        <span>Sign Out</span>
      </button>
    </div>
  );
}
